import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/images/roster_connect_icon.png";
import main_screenshot from "../assets/images/main_screenshot.png";
import appstore from "../assets/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import "./RosterConnect.css";

const LINK_TO_APPSTORE = "#";
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const FEATURES = [
  {
    icon: "📄",
    title: "PDF Reading",
    desc: "Upload your roster as a PDF — Roster Connect automatically decodes your shifts and displays them clearly.",
  },
  {
    icon: "📅",
    title: "Calendar Export",
    desc: "Export all your shifts directly to your preferred calendar. Compatible with Apple, Google, and Outlook.",
  },
  {
    icon: "🔔",
    title: "Overview at a Glance",
    desc: "See your week and month in an intuitive layout that makes it easy to plan your free time.",
  },
  {
    icon: "🔒",
    title: "Private & Secure",
    desc: "Your rosters belong to you. We never share your data and only store what is strictly necessary.",
  },
];

function RCNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const go = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <nav className={`rc-nav ${scrolled ? "scrolled" : ""}`}>
      <div className="rc-nav-inner">
        <Link to="/" className="rc-nav-back">
          ← CR Software
        </Link>
        <div className="rc-logo">
          <img className="rc-logo-icon" src={logo} alt="Logo" />
          <span>Roster Connect</span>
        </div>
        <div className="rc-nav-right">
          <button onClick={() => go("features")}>Features</button>

          <a href={LINK_TO_APPSTORE}>
            <img className="" src={appstore} alt="Apple AppStore" />
          </a>
        </div>
      </div>
    </nav>
  );
}

function RCHero() {
  return (
    <section className="rc-hero">
      <div className="rc-hero-inner">
        <div className="rc-hero-text">
          <div className="rc-eyebrow">
            <span className="rc-pulse" />
            Available now
          </div>
          <h1>
            Your Roster.
            <br />
            <span className="rc-accent">Simplified.</span>
          </h1>
          <p className="rc-hero-sub">
            Roster Connect transforms cluttered PDF rosters into an intuitive
            overview — and exports your shifts directly to your calendar.
          </p>
          <div className="rc-hero-actions">
            <button
              className="rc-btn-primary"
              onClick={() =>
                document
                  .getElementById("download")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Download
            </button>
            <button
              className="rc-btn-ghost"
              onClick={() =>
                document
                  .getElementById("features")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              See how it works
            </button>
          </div>
          <div className="rc-hero-legal">
            <Link to="/roster-connect/privacy">Privacy Policy</Link>
            <span>·</span>
            <Link to="/roster-connect/terms">Terms and Conditions</Link>
            <span>·</span>
            <Link to="/roster-connect/support">Support</Link>
          </div>
        </div>
        <div className="rc-hero-phone">
          <div className="rc-phone-frame">
            <div className="rc-phone-notch" />
            <img
              src={main_screenshot}
              style={{ borderRadius: 30 }}
              alt="Logo"
            ></img>
          </div>
        </div>
      </div>
    </section>
  );
}

function RCFeatures() {
  const [ref, inView] = useInView();
  return (
    <section className="rc-section rc-features" id="features" ref={ref}>
      <div className="rc-section-inner">
        <div className="rc-section-label">Features</div>
        <h2>Everything you need</h2>
        <div className={`rc-features-grid ${inView ? "in" : ""}`}>
          {FEATURES.map((f, i) => (
            <div
              className="rc-feature-card"
              key={f.title}
              style={{ "--d": `${i * 0.1}s` }}
            >
              <div className="rc-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RCHowItWorks() {
  const [ref, inView] = useInView();
  return (
    <section className="rc-section rc-how" ref={ref}>
      <div className="rc-section-inner">
        <div className="rc-section-label">How it works</div>
        <h2>Three simple steps</h2>
        <div className={`rc-steps ${inView ? "in" : ""}`}>
          {[
            {
              n: "1",
              title: "Upload your PDF",
              desc: "Select your roster PDF directly from the app.",
            },
            {
              n: "2",
              title: "See your overview",
              desc: "Your shifts are automatically displayed in a clear and intuitive design.",
            },
            {
              n: "3",
              title: "Export",
              desc: "Send all shifts to your calendar with a single tap.",
            },
          ].map((s, i) => (
            <div
              className="rc-step"
              key={s.n}
              style={{ "--d": `${i * 0.12}s` }}
            >
              <div className="rc-step-num">{s.n}</div>
              <div className="rc-step-bar" />
              <h4>{s.title}</h4>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RCPricing() {
  const [ref, inView] = useInView();
  return (
    <section className="rc-section rc-pricing" id="pricing" ref={ref}>
      <div className="rc-section-inner">
        <div className="rc-section-label">Pricing</div>
        <h2>Simple and transparent</h2>
        <div className={`rc-plans ${inView ? "in" : ""}`}>
          <div className="rc-plan">
            <div className="rc-plan-name">Free</div>
            <div className="rc-plan-price">
              $0<span>/mo</span>
            </div>
            <ul>
              <li>✓ PDF Reading</li>
              <li>✓ Week view</li>
              <li>✓ Up to 3 exports/mo</li>
            </ul>
            <button
              className="rc-btn-ghost rc-plan-btn"
              onClick={() =>
                document
                  .getElementById("download")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Get started
            </button>
          </div>
          <div className="rc-plan featured">
            <div className="rc-plan-badge">Most popular</div>
            <div className="rc-plan-name">Pro</div>
            <div className="rc-plan-price">
              $4<span>/mo</span>
            </div>
            <ul>
              <li>✓ Everything in Free</li>
              <li>✓ Unlimited exports</li>
              <li>✓ Monthly and yearly views</li>
              <li>✓ Notifications</li>
              <li>✓ Priority support</li>
            </ul>
            <button
              className="rc-btn-primary rc-plan-btn"
              onClick={() =>
                document
                  .getElementById("download")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Start free trial
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function RCDownload() {
  return (
    <section className="rc-section rc-download" id="download">
      <div className="rc-section-inner rc-download-inner">
        <div>
          <div className="rc-section-label">Download</div>
          <h2>Ready to try?</h2>
          <p>
            Get Roster Connect and turn your roster into an overview you can
            actually use.
          </p>
          <div className="rc-store-btns">
            <a href={LINK_TO_APPSTORE}>
              <img className="" src={appstore} alt="Apple AppStore" />
            </a>
          </div>
        </div>
        <div className="rc-download-legal">
          <p>Questions or feedback?</p>
          <a href="mailto:contact@crsoftware.dk">contact@crsoftware.dk</a>
          <div className="rc-legal-links">
            <Link to="/roster-connect/privacy">Privacy Policy</Link>
            <Link to="/roster-connect/terms">Terms and Conditions</Link>
            <Link to="/roster-connect/support">Support</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function RCFooter() {
  return (
    <footer className="rc-footer">
      <div className="rc-footer-inner">
        <div className="rc-logo">
          <img className="rc-logo-icon sm" src={logo} alt="Logo" />
          <span>Roster Connect</span>
        </div>
        <span className="rc-footer-sep">·</span>
        <span className="rc-footer-by">
          A product by <Link to="/">CR Software ApS</Link>
        </span>
        <div className="rc-footer-links">
          <Link to="/roster-connect/privacy">Privacy Policy</Link>
          <Link to="/roster-connect/terms">Terms</Link>
          <Link to="/roster-connect/support">Support</Link>
        </div>
        <span className="rc-footer-copy">© {new Date().getFullYear()}</span>
      </div>
    </footer>
  );
}

export default function RosterConnect() {
  return (
    <div className="rc-root">
      <RCNav />
      <RCHero />
      <RCFeatures />
      <RCDownload />
      <RCFooter />
    </div>
  );
}
